import React from 'react';
import { Icon } from '../Icon';
import { usePremiumFeature } from '../../hooks/usePremiumFeature';
import { FEATURES, FeatureType } from '../../config/features';
import './PremiumFeature.css';

interface PremiumFeatureProps {
  featureId: FeatureType;
  children: React.ReactNode;
  // Показывать ли детям с блюром или просто кнопку апгрейда
  showBlurred?: boolean;
  // Кастомное сообщение
  customMessage?: string;
}

/**
 * Компонент-обёртка для премиум функций
 * Если у пользователя нет доступа - показывает заглушку с предложением апгрейда
 * 
 * @example
 * ```tsx
 * <PremiumFeature featureId="ai_chat">
 *   <AIChat />
 * </PremiumFeature>
 * ```
 */
export const PremiumFeature: React.FC<PremiumFeatureProps> = ({
  featureId,
  children,
  showBlurred = false,
  customMessage,
}) => {
  const { hasAccess, requestUpgrade, limit, remaining } = usePremiumFeature(featureId);
  const feature = FEATURES[featureId];
  
  // Если есть доступ - просто рендерим детей
  if (hasAccess) {
    return <>{children}</>;
  }
  
  // Если нет доступа - показываем заглушку
  return (
    <div className="premium-feature-wrapper">
      {showBlurred && (
        <div className="premium-feature-content--blurred">
          {children}
        </div>
      )}
      
      <div className="premium-feature-overlay">
        <div className="premium-feature-card neo-raised">
          <div className="premium-feature-icon">
            <Icon name="lock" size={48} color="#FF8C00" />
          </div>
          
          <h3 className="premium-feature-title">
            {feature.name}
          </h3>
          
          <p className="premium-feature-description">
            {customMessage || feature.description}
          </p>
          
          {limit && (
            <div className="premium-feature-limit">
              <Icon name="info" size={16} color="#FF8C00" />
              <span>{feature.limitDescription}</span>
            </div>
          )}
          
          <button
            className="premium-feature-button neo-raised"
            onClick={requestUpgrade}
          >
            <Icon name="star" size={20} color="#FF8C00" />
            <span>Апгрейд до Premium</span>
          </button>
          
          <div className="premium-feature-benefits">
            <div className="premium-feature-benefit">
              <Icon name="check" size={16} color="#34C759" />
              <span>Без ограничений</span>
            </div>
            <div className="premium-feature-benefit">
              <Icon name="check" size={16} color="#34C759" />
              <span>Все премиум функции</span>
            </div>
            <div className="premium-feature-benefit">
              <Icon name="check" size={16} color="#34C759" />
              <span>Приоритетная поддержка</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Компонент бейджа "Premium"
 */
export const PremiumBadge: React.FC<{ size?: 'small' | 'medium' }> = ({ size = 'medium' }) => {
  return (
    <span className={`premium-badge premium-badge--${size}`}>
      <Icon name="star" size={size === 'small' ? 12 : 16} color="#FF8C00" />
      <span>Premium</span>
    </span>
  );
};

/**
 * Компонент для кнопок с премиум функциями
 */
interface PremiumButtonProps {
  featureId: FeatureType;
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

export const PremiumButton: React.FC<PremiumButtonProps> = ({
  featureId,
  onClick,
  children,
  className = '',
  disabled = false,
}) => {
  const { hasAccess, requestUpgrade } = usePremiumFeature(featureId);
  
  const handleClick = () => {
    if (!hasAccess) {
      requestUpgrade();
      return;
    }
    
    onClick?.();
  };
  
  return (
    <button
      className={`${className} ${!hasAccess ? 'premium-button--locked' : ''}`}
      onClick={handleClick}
      disabled={disabled}
    >
      {children}
      {!hasAccess && (
        <Icon name="lock" size={16} color="#FF8C00" style={{ marginLeft: '8px' }} />
      )}
    </button>
  );
};

